/**
 * Wrong tries at the owner's PIN, one after another, for the home screen.
 *
 * The home screen said so after three in a day, from any device and however far apart. The owner,
 * slipping once or twice a day unlocking their own phone, was told so nearly every day (2026-10-15:
 * «it's too spammy»). Now it takes ten in a row on one device: wrong tries at the owner's PIN with
 * nothing between them that only the owner's PIN lets through — the owner getting in, doing
 * something in their own name, or letting something through on somebody else's sign-in. A slip
 * followed by the right PIN starts again from nothing; somebody trying, with sales or sign-ins of
 * their own in between, does not. Kept per device, like the wait after the third wrong PIN
 * (`intentos.ts`): it is the hands on one device that are trying.
 */

import { db, type LocalEvent } from '../db/dexie.js';

/** Wrong tries in a row at the owner's PIN, on one device, that the home screen tells the owner about. */
export const SEGUIDOS_PARA_AVISAR = 10;

type Registro = Pick<LocalEvent, 'type' | 'payload' | 'actorId'>;

/** A wrong try at the owner's PIN: typed into an authorisation, or on an owner's own account. */
export function esIntentoAlDueno(e: Registro, duenos: ReadonlySet<string>): boolean {
  return (
    e.type === 'sesion.pin_incorrecto' &&
    ((e.payload as { donde?: string }).donde === 'autorizacion' || duenos.has(e.actorId))
  );
}

/**
 * Where a record names who let it through: `autorizadoPor` and the like (`listaAutorizadaPor`,
 * `faltaAutorizadaPor`, `apartadoAutorizadoPor`), and who received the cash, `recibidoPor`.
 */
const QUIEN_LO_DEJO = /[aA]utorizad[oa]Por$|^recibidoPor$/;

/** Whether `valor` names an owner as who let it through, on itself or on one of its lines. */
function loDejoUnDueno(valor: unknown, duenos: ReadonlySet<string>): boolean {
  if (Array.isArray(valor)) return valor.some((v) => loDejoUnDueno(v, duenos));
  if (!valor || typeof valor !== 'object') return false;
  return Object.entries(valor).some(([clave, v]) =>
    typeof v === 'string' ? QUIEN_LO_DEJO.test(clave) && duenos.has(v) : loDejoUnDueno(v, duenos),
  );
}

/** What the owner's PIN typed right leaves on a device: the owner got in, did something, or let it through. */
export function conElPinDelDueno(e: Registro, duenos: ReadonlySet<string>): boolean {
  return e.type !== 'sesion.pin_incorrecto' && (duenos.has(e.actorId) || loDejoUnDueno(e.payload, duenos));
}

/** The longest run of wrong tries at the owner's PIN in one device's records, in the order it made them. */
export function masSeguidos(registros: readonly Registro[], duenos: ReadonlySet<string>): number {
  let racha = 0;
  let mayor = 0;
  for (const e of registros) {
    if (esIntentoAlDueno(e, duenos)) mayor = Math.max(mayor, ++racha);
    else if (conElPinDelDueno(e, duenos)) racha = 0;
  }
  return mayor;
}

/**
 * The longest run of wrong tries at the owner's PIN on any one device since `desde` (an ISO
 * moment). Of each device, only what it recorded between its first and its last try is read.
 */
export async function seguidosAlDueno(desde: string): Promise<number> {
  const duenos = new Set((await db.usuarios.toArray()).filter((u) => u.rol === 'dueno').map((u) => u.id));
  const intentos = (await db.eventos.where('type').equals('sesion.pin_incorrecto').toArray()).filter(
    (e) => e.clientTs >= desde && esIntentoAlDueno(e, duenos),
  );
  const porEquipo = new Map<string, number[]>();
  for (const e of intentos) porEquipo.set(e.deviceId, [...(porEquipo.get(e.deviceId) ?? []), e.deviceSeq]);
  let mayor = 0;
  for (const [equipo, numeros] of porEquipo) {
    const entre = await db.eventos
      .where('deviceSeq')
      .between(Math.min(...numeros), Math.max(...numeros), true, true)
      .filter((e) => e.deviceId === equipo)
      .sortBy('deviceSeq');
    mayor = Math.max(mayor, masSeguidos(entre, duenos));
  }
  return mayor;
}
